import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  addDays,
  addMonths,
  endOfMonth,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from 'date-fns'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { formatDateToISO, parseDateString } from '../utils/noteDateUtils'
import { useDateTimeFormatters } from '@/lib/i18n/useDateTimeFormatters'
import { useWeekStartsOn } from '@/store/UserPreferencesStore'
import { cn } from '@/lib/utils'

interface NotesCalendarProps {
  /** yyyy-MM-dd of the note being viewed. */
  selectedDate: string
  /** yyyy-MM-dd for today in the user's timezone. */
  today: string
  /** Days that have a note. */
  noteDates: ReadonlySet<string>
  onSelectDate: (date: string) => void
}

/**
 * Navigate notes by calendar, not a list (design 10a): a month with a dot on
 * each day that has a note. Days after today can't be opened — no writing
 * ahead (matches mobile).
 */
export function NotesCalendar({ selectedDate, today, noteDates, onSelectDate }: NotesCalendarProps) {
  const { t } = useTranslation('notes')
  const { date: formatDate } = useDateTimeFormatters()
  const weekStartsOn = useWeekStartsOn()
  const [month, setMonth] = useState(() => startOfMonth(parseDateString(selectedDate) ?? new Date()))

  const days = useMemo(() => {
    const first = startOfWeek(startOfMonth(month), { weekStartsOn })
    const last = endOfMonth(month)
    const out: Array<Date> = []
    for (let day = first; day <= last || out.length % 7 !== 0; day = addDays(day, 1)) out.push(day)
    return out
  }, [month, weekStartsOn])

  const weekdayLabels = useMemo(
    () => days.slice(0, 7).map((d) => formatDate(d, { weekday: 'narrow' })),
    [days, formatDate],
  )

  return (
    <div className="rounded-lg border bg-card p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[13px] font-semibold">{formatDate(month, { month: 'long', year: 'numeric' })}</span>
        <div className="flex items-center">
          <button
            type="button"
            aria-label={t('previousMonth')}
            onClick={() => setMonth((m) => addMonths(m, -1))}
            className="flex size-6 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            <ChevronLeft className="size-3.5" />
          </button>
          <button
            type="button"
            aria-label={t('nextMonth')}
            onClick={() => setMonth((m) => addMonths(m, 1))}
            className="flex size-6 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            <ChevronRight className="size-3.5" />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-y-0.5 text-center">
        {weekdayLabels.map((label, i) => (
          <span key={i} className="pb-1 text-[10.5px] font-medium text-muted-foreground">
            {label}
          </span>
        ))}
        {days.map((day) => {
          const iso = formatDateToISO(day)
          const inMonth = isSameMonth(day, month)
          const isSelected = iso === selectedDate
          const isToday = iso === today
          const hasNote = noteDates.has(iso)
          const isFuture = iso > today
          return (
            <button
              key={iso}
              type="button"
              disabled={isFuture}
              onClick={() => onSelectDate(iso)}
              aria-current={isSelected ? 'date' : undefined}
              aria-label={`${formatDate(day, { weekday: 'long', month: 'long', day: 'numeric' })}${hasNote ? ` · ${t('hasNote')}` : ''}`}
              className={cn(
                'relative mx-auto flex size-8 flex-col items-center justify-center rounded-md text-[12.5px] tabular-nums transition-colors',
                !inMonth && 'text-muted-foreground/50',
                inMonth && !isSelected && !isFuture && 'hover:bg-secondary',
                isFuture && 'cursor-default text-muted-foreground/50',
                isSelected && 'bg-primary font-semibold text-primary-foreground',
                !isSelected && isToday && 'font-semibold text-brand',
              )}
            >
              {day.getDate()}
              {hasNote && (
                <span
                  className={cn(
                    'absolute bottom-1 size-1 rounded-full',
                    isSelected ? 'bg-brand' : 'bg-brand/80',
                  )}
                />
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
